import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-scroll';
import { motion } from 'framer-motion';
import { FaLinkedin, FaEnvelope, FaPhone, FaArrowUp, FaMapMarkerAlt } from 'react-icons/fa';
import LogoImage from '../assets/logo.png'; // Update this path if needed

const Footer = () => {
  const currentYear = new Date().getFullYear();
  
  return (
    <FooterContainer>
      <FooterTop>
        <div className="container">
          <FooterContent>
            <FooterBrand>
              <BrandContainer>
                <LogoWrapper>
                  <img src={LogoImage} alt="The Coach House Logo" />
                </LogoWrapper>
                <BrandText>
                  <h3>The Coach House</h3>
                  <span>with Samantha Knowles</span>
                </BrandText>
              </BrandContainer>
              <BrandDescription>
                Transforming lives through clarity, confidence, and personal growth. 
                Professional coaching that helps you progress with purpose.
              </BrandDescription>
              <FooterSocial>
                <SocialLink href="https://linkedin.com/in/samanthaknowles" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn Profile">
                  <FaLinkedin />
                </SocialLink>
                <SocialLink href="mailto:info@samanthaknowles.com" aria-label="Email">
                  <FaEnvelope />
                </SocialLink>
                <SocialLink href="tel:+33785813680" aria-label="Phone">
                  <FaPhone />
                </SocialLink>
              </FooterSocial>
            </FooterBrand>
            
            <FooterNavigation>
              <FooterNavColumn>
                <FooterColumnTitle>Quick Links</FooterColumnTitle>
                <FooterNavList>
                  <FooterNavItem>
                    <Link to="about" smooth={true} duration={800}>About</Link>
                  </FooterNavItem>
                  <FooterNavItem>
                    <Link to="services" smooth={true} duration={800}>Services</Link>
                  </FooterNavItem>
                  <FooterNavItem>
                    <Link to="testimonials" smooth={true} duration={800}>Testimonials</Link>
                  </FooterNavItem>
                  <FooterNavItem>
                    <Link to="process" smooth={true} duration={800}>Process</Link>
                  </FooterNavItem>
                  <FooterNavItem>
                    <Link to="contact" smooth={true} duration={800}>Contact</Link>
                  </FooterNavItem>
                </FooterNavList>
              </FooterNavColumn>
              
              <FooterNavColumn>
                <FooterColumnTitle>Services</FooterColumnTitle>
                <FooterNavList>
                  <FooterNavItem>
                    <Link to="services" smooth={true} duration={800}>Individual Coaching</Link>
                  </FooterNavItem>
                  <FooterNavItem>
                    <Link to="services" smooth={true} duration={800}>Team Coaching</Link>
                  </FooterNavItem>
                  <FooterNavItem>
                    <Link to="services" smooth={true} duration={800}>Executive Leadership</Link>
                  </FooterNavItem>
                </FooterNavList>
              </FooterNavColumn>
              
              <FooterNavColumn>
                <FooterColumnTitle>Contact</FooterColumnTitle>
                <ContactList>
                  <ContactItem>
                    <FaPhone size={14} />
                    <span>+33 7 85 81 36 80</span>
                  </ContactItem>
                  <ContactItem>
                    <FaEnvelope size={14} />
                    <span>info@samanthaknowles.com</span>
                  </ContactItem>
                  <ContactItem>
                    <FaMapMarkerAlt size={14} />
                    <span>Virtual worldwide coaching</span>
                  </ContactItem>
                </ContactList>
              </FooterNavColumn>
            </FooterNavigation>
          </FooterContent>
        </div>
      </FooterTop>
      
      <FooterBottom>
        <div className="container">
          <CopyrightText>
            &copy; {currentYear} The Coach House with Samantha Knowles. All rights reserved.
          </CopyrightText>
          
          <FooterLegal>
            <LegalLink href="/privacy-policy">Privacy Policy</LegalLink>
            <LegalSeparator>|</LegalSeparator>
            <LegalLink href="/terms">Terms & Conditions</LegalLink>
          </FooterLegal>
        </div>
      </FooterBottom>
      
      <BackToTopButton>
        <Link to="top" smooth={true} duration={800} aria-label="Back to top">
          <FaArrowUp />
        </Link>
      </BackToTopButton>
      
      <FooterDecoration className="decoration-1" />
      <FooterDecoration className="decoration-2" />
    </FooterContainer>
  );
};

const FooterContainer = styled.footer`
  position: relative;
  overflow: hidden;
  background-color: #1A2A3A;
  color: white;
`;

const FooterTop = styled.div`
  padding: 80px 0 60px;
  position: relative;
  z-index: 2;
  
  @media (max-width: 768px) {
    padding: 60px 0 40px;
  }
`;

const FooterContent = styled.div`
  display: flex;
  justify-content: space-between;
  
  @media (max-width: 992px) {
    flex-direction: column;
  }
`;

const FooterBrand = styled.div`
  flex: 0.35;
  
  @media (max-width: 992px) {
    margin-bottom: 40px;
  }
`;

const BrandContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 20px;
`;

const LogoWrapper = styled.div`
  height: 40px;
  margin-right: 15px;
  
  img {
    height: 100%;
    width: auto;
    filter: brightness(0) invert(1);
  }
`;

const BrandText = styled.div`
  h3 {
    font-size: 1.5rem;
    margin: 0;
    line-height: 1.2;
  }
  
  span {
    font-size: 0.85rem;
    color: #A8C4A0;
  }
`;

const BrandDescription = styled.p`
  color: #ccc;
  line-height: 1.6;
  margin-bottom: 25px;
  font-size: 0.95rem;
  max-width: 90%;
`;

const FooterSocial = styled.div`
  display: flex;
  gap: 12px;
`;

const SocialLink = styled.a`
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1rem;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: #A8C4A0;
    transform: translateY(-3px);
  }
`;

const FooterNavigation = styled.div`
  flex: 0.65;
  display: flex;
  justify-content: space-between;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const FooterNavColumn = styled.div`
  @media (max-width: 768px) {
    margin-bottom: 30px;
    
    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const FooterColumnTitle = styled.h4`
  font-size: 1.1rem;
  margin-bottom: 20px;
  color: white;
  position: relative;
  padding-bottom: 10px;
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 30px;
    height: 2px;
    background-color: #A8C4A0;
  }
`;

const FooterNavList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const FooterNavItem = styled.li`
  margin-bottom: 12px;
  
  a {
    color: #ccc;
    text-decoration: none;
    transition: all 0.2s ease;
    font-size: 0.95rem;
    display: inline-block;
    
    &:hover {
      color: #A8C4A0;
      transform: translateX(3px);
    }
  }
`;

const ContactList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  color: #ccc;
  font-size: 0.95rem;
  
  svg {
    margin-right: 10px;
    color: #A8C4A0;
  }
`;

const FooterBottom = styled.div`
  padding: 25px 0;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  position: relative;
  z-index: 2;
  
  .container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    
    @media (max-width: 768px) {
      flex-direction: column;
      gap: 15px;
    }
  }
`;

const CopyrightText = styled.p`
  color: #aaa;
  font-size: 0.85rem;
  margin: 0;
`;

const FooterLegal = styled.div`
  display: flex;
  align-items: center;
`;

const LegalLink = styled.a`
  color: #aaa;
  font-size: 0.85rem;
  text-decoration: none;
  transition: color 0.2s ease;
  
  &:hover {
    color: #A8C4A0;
  }
`;

const LegalSeparator = styled.span`
  margin: 0 10px;
  color: #666;
`;

const BackToTopButton = styled.div`
  position: fixed;
  bottom: 30px;
  right: 30px;
  width: 45px;
  height: 45px;
  background-color: #A8C4A0;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
  transition: all 0.3s ease;
  z-index: 999;
  
  &:hover {
    background-color: #5A7F8C;
    transform: translateY(-5px);
  }
  
  @media (max-width: 768px) {
    bottom: 20px;
    right: 20px;
    width: 40px;
    height: 40px;
  }
`;

const FooterDecoration = styled.div`
  position: absolute;
  border-radius: 30% 70% 70% 30% / 30% 30% 70% 70%;
  z-index: 1;
  opacity: 0.03;
  
  &.decoration-1 {
    width: 400px;
    height: 400px;
    background-color: #A8C4A0;
    top: -200px;
    right: 10%;
  }
  
  &.decoration-2 {
    width: 350px;
    height: 350px;
    background-color: #5A7F8C;
    bottom: -150px;
    left: 5%;
  }
`;

export default Footer;